// SCRUM-1627 — durable Research-report generation watcher.
//
// The completion toast used to live inside StockReportComponent's poll loop,
// which was torn down in ngOnDestroy — so navigating away mid-generation
// killed the "ready" notification. This root-scoped service owns the
// poll-to-completion loop instead, so the toast fires even after the user
// has left the report page. Components subscribe to settled$ to refresh
// their own view when they are still mounted.

import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { StockReportService } from './stock-report.service';
import { NotificationService } from '../../../services/notification.service';

const POLL_MS = 5000;
const MAX_POLLS = 24;

export interface ReportSettled {
  ticker: string;
  ready: boolean; // true = a fresh report landed; false = timed out
}

@Injectable({ providedIn: 'root' })
export class ReportGenerationService {
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly _settled = new Subject<ReportSettled>();
  /** Emits once per watched ticker when generation completes or times out. */
  readonly settled$: Observable<ReportSettled> = this._settled.asObservable();

  constructor(
    private readonly svc: StockReportService,
    private readonly notify: NotificationService,
  ) {}

  /** True while a generation for this ticker is being watched. */
  isWatching(ticker: string): boolean {
    return this.timers.has(ticker.toUpperCase());
  }

  /**
   * Poll /meta until generated_at advances past `before` (or any report
   * appears when there was none), then fire the completion toast. Survives
   * navigation because this service is root-scoped, not component-scoped.
   * De-duplicates: a second watch() for a ticker already in flight is a no-op.
   */
  watch(ticker: string, before: string | null): void {
    const key = ticker.toUpperCase();
    if (this.timers.has(key)) return;
    this.poll(key, before, 0);
  }

  private poll(ticker: string, before: string | null, attempt: number): void {
    if (attempt >= MAX_POLLS) {
      this.timers.delete(ticker);
      this.notify.info(`Still generating the Research report for ${ticker}. Refresh shortly.`);
      this._settled.next({ ticker, ready: false });
      return;
    }
    const t = setTimeout(() => {
      this.svc.getMeta(ticker).subscribe({
        next: (meta) => {
          if (before === null || meta.generated_at !== before) {
            this.timers.delete(ticker);
            this.notify.success(`Research report ready for ${ticker}.`);
            this._settled.next({ ticker, ready: true });
          } else {
            this.poll(ticker, before, attempt + 1);
          }
        },
        error: () => this.poll(ticker, before, attempt + 1),
      });
    }, POLL_MS);
    this.timers.set(ticker, t);
  }
}
